import { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { reviewService } from '@services/reviewService';
import { WorkpayApiError } from '@workpay/ds-shared/services/apiEnvelope';
import { Button } from '@workpay/ds-shared/components/ui/Button';
import { ChatWidget } from '@workpay/ds-shared/components/ChatWidget';
import { PdfReviewViewer } from '@workpay/ds-shared/components/PdfReviewViewer';
import { useReviewSse } from '@workpay/ds-shared/hooks/useReviewSse';
import { useDsConfig } from '@workpay/ds-shared/context/DsProvider';
import { API_BASE } from '@lib/runtimeConfig';
import { useTranslation } from '@hooks/useTranslation';
import { LocaleSwitcher } from '@components/LocaleSwitcher/LocaleSwitcher';
import type { DocumentReview, ReviewActivity } from '@workpay/ds-shared/types/review';
import styles from './ReviewPage.module.scss';

// External reviewer file URL resolver — external sessions don't have file-management
// access, so file fetches route through contract-service's onlyoffice file endpoint.
// Backend gap: `/v1/onlyoffice/file/{fileId}` is not documented for contract-service
// (flagged in PLAN-envelope-phase3.md §Backend gaps). Using the same path pattern
// document-signing used pre-migration until the backend confirms routing.
function resolveReviewerFileUrl(fileId: string): string {
  // Must be absolute on deployed envs — a relative URL resolves against the
  // Pages origin (external.<apex>), which serves the SPA's index.html for any
  // /api/* path. API_BASE is '' on localhost (Vite proxy handles it).
  return `${API_BASE}/api/contract-service/v1/onlyoffice/file/${fileId}`;
}

interface ReviewPageProps {
  token: string;
}

export function ReviewPage({ token }: ReviewPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [completed, setCompleted] = useState(false);

  const { data: session, isLoading, error } = useQuery({
    queryKey: ['review-session', token],
    queryFn: () => reviewService.loadSession(token),
    retry: false,
  });

  const documents = useMemo(() => session?.documents ?? [], [session?.documents]);
  const isMultiDoc = documents.length > 1;
  const [activeDocId, setActiveDocId] = useState<string | null>(null);
  // Derive resolved doc id — falls back to first document if none selected
  const resolvedDocId = activeDocId ?? documents[0]?.id ?? null;
  const activeDocument = documents.find(d => d.id === resolvedDocId) ?? null;

  const { data: reviewHistory = [] } = useQuery({
    queryKey: ['review-history', token],
    queryFn: () => reviewService.getReviewHistory(token),
    enabled: !!session,
    refetchInterval: 10_000,
  });

  const { data: sessionActivities = [] } = useQuery({
    queryKey: ['review-activities', token],
    queryFn: () => reviewService.getSessionActivities(token),
    enabled: !!session && reviewHistory.length === 0,
    refetchInterval: 10_000,
  });

  const reviews: DocumentReview[] = useMemo(() => {
    if (reviewHistory.length > 0) return reviewHistory;
    if (!session) return [];
    return [{
      id: session.reviewId,
      targetType: session.targetType,
      targetId: session.targetId ?? '',
      fileId: session.fileId,
      status: session.reviewStatus as DocumentReview['status'],
      initiatedById: '',
      initiatedByName: '',
      initiatedByEmail: '',
      message: session.message,
      authorCompleted: false,
      reviewerCompleted: false,
      reviewers: [],
      activities: sessionActivities as ReviewActivity[],
      createdOn: new Date().toISOString(),
      updatedOn: new Date().toISOString(),
    }];
  }, [reviewHistory, session, sessionActivities]);

  useReviewSse({
    mode: 'reviewer',
    token,
    targetType: session?.targetType,
    targetId: session?.targetId,
    enabled: !!session,
    // ds-shared defaults baseUrl to the relative '/api/contract-service', which
    // resolves against the Pages origin on deployed envs (EventSource then gets
    // index.html / text/html and silently dies). Same fix as tenant-onboarding's
    // 2026-06-19 SSE baseUrl change; API_BASE is '' locally so dev is unchanged.
    baseUrl: `${API_BASE}/api/contract-service`,
  });

  const isReviewCompleted = completed || session?.reviewStatus === 'COMPLETED' || session?.reviewStatus === 'CANCELLED';

  /**
   * A refusal has to be SHOWN. Neither mutation carried an `onError`, so a server refusal —
   * most often `43121 K_REVIEWER_ALREADY_FINISHED` — left the reviewer pressing a button that
   * did nothing at all, with no message anywhere. `description` on a refusal is a translation
   * KEY, not a sentence, so it goes through `t()`; rendering it raw would put a `K_` token in
   * front of an external reviewer.
   */
  const [actionError, setActionError] = useState<string | null>(null);
  const actionErrorFrom = (err: unknown): string => {
    const e = err as { description?: string; message?: string };
    const generic = t('K_DS_EXT_REVIEW_ACTION_FAILED', 'That did not go through. Try again.');
    if (e?.description) {
      // Empty fallback on purpose: `t` returns it when the bundle has no value for the key,
      // which is how an unseeded refusal is caught before it reaches the screen as a token.
      const translated = t(e.description, '');
      if (translated) return translated;
    }
    return e?.message || generic;
  };

  const submitMutation = useMutation({
    mutationFn: () => reviewService.submitChanges(token),
    onSuccess: () => {
      setActionError(null);
      queryClient.invalidateQueries({ queryKey: ['review-history', token] });
      queryClient.invalidateQueries({ queryKey: ['review-activities', token] });
    },
    onError: (err) => setActionError(actionErrorFrom(err)),
  });

  const completeMutation = useMutation({
    mutationFn: () => reviewService.markReviewerComplete(token),
    onSuccess: () => {
      setActionError(null);
      setCompleted(true);
    },
    onError: (err) => setActionError(actionErrorFrom(err)),
  });

  // Both buttons disable while either mutation is in flight — prevents the
  // unclicked button from staying interactive during the API call and keeps
  // the loader visible on the clicked one until the response arrives.
  const anyActionPending = submitMutation.isPending || completeMutation.isPending;

  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>{t('K_DS_EXT_REVIEW_LOADING_SESSION', 'Loading review session...')}</div>
      </div>
    );
  }

  if (error || !session) {
    // Review-ended / gone codes from the reviewer-token endpoint → benign
    // "Review Complete" screen instead of a raw error:
    //   43069 K_REVIEW_NOT_FOUND, 43081 K_REVIEW_TERMINAL_STATUS,
    //   43053 K_NO_ACTIVE_REVIEW,
    //   43096 K_CONTRACT_NOT_FOUND — once the reviewer completes, the snapshot is
    //         parked (REVIEW_COMPLETED) and the token-scoped contract lookup 404s,
    //         so a re-opened mail link / refresh lands here.
    const apiErr = error instanceof WorkpayApiError ? error : null;
    const isReviewEnded = apiErr !== null && [43069, 43081, 43053, 43096].includes(apiErr.code);
    return (
      <div className={styles.container}>
        <div className={isReviewEnded ? styles.done : styles.error}>
          <h2>{isReviewEnded ? t('K_DS_STATUS_REVIEW_COMPLETED', 'Review Complete') : t('K_DS_EXT_REVIEW_ERROR_LOAD_TITLE', 'Unable to load review')}</h2>
          <p>{isReviewEnded ? t('K_DS_EXT_REVIEW_ENDED_BODY', 'This review has been completed. No further changes can be made. Thank you for your participation.') : (error as Error)?.message || t('K_DS_EXT_REVIEW_ERROR_LOAD_BODY', 'This review link may be invalid or expired.')}</p>
        </div>
      </div>
    );
  }

  if (isReviewCompleted) {
    return (
      <div className={styles.container}>
        <div className={styles.done}>
          <h2>{t('K_DS_STATUS_REVIEW_COMPLETED', 'Review Complete')}</h2>
          <p>{session
            ? t('K_DS_EXT_REVIEW_COMPLETED_BODY', 'Thank you, {name}! This review has been completed.').replace('{name}', session.reviewerName)
            : t('K_DS_EXT_REVIEW_COMPLETED_BODY_NO_NAME', 'Thank you! This review has been completed.')}</p>
          <p>{t('K_DS_EXT_REVIEW_COMPLETED_BODY_HINT', 'No further changes can be made.')}</p>
        </div>
      </div>
    );
  }

  const displayTitle = isMultiDoc && activeDocument
    ? activeDocument.documentName
    : session.documentTitle;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>{displayTitle}</h1>
          <span className={styles.reviewerName}>{t('K_DS_EXT_REVIEW_HEADER_REVIEWING_AS', 'Reviewing as {name}').replace('{name}', session.reviewerName)}</span>
        </div>
        <div className={styles.headerRight}>
          <LocaleSwitcher />
          <Button
            variant="secondary"
            size="sm"
            loading={submitMutation.isPending}
            disabled={anyActionPending}
            onClick={() => submitMutation.mutate()}
          >
            {t('K_DS_EXT_REVIEW_BUTTON_SUBMIT_CHANGES', 'Submit Changes')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={completeMutation.isPending}
            disabled={anyActionPending}
            onClick={() => completeMutation.mutate()}
          >
            {t('K_DS_EXT_REVIEW_BUTTON_MARK_COMPLETE', 'Mark Complete')}
          </Button>
        </div>
      </div>

      {/* A refused action used to be completely silent: neither mutation had an `onError`, so
          the reviewer pressed a button and nothing happened, with nothing to read anywhere. */}
      {actionError && (
        <p className={styles.actionError} role="alert">
          {actionError}
        </p>
      )}

      <div className={styles.body}>
        {isMultiDoc && (
          <div className={styles.docSidebar}>
            <div className={styles.docSidebarTitle}>{t('K_DOCUMENTS', 'Documents')}</div>
            <div className={styles.docSidebarList}>
              {documents.map(doc => (
                <button
                  key={doc.id}
                  className={`${styles.docSidebarItem} ${doc.id === resolvedDocId ? styles.docSidebarItemActive : ''}`}
                  onClick={() => setActiveDocId(doc.id)}
                >
                  <span className={styles.docSidebarIcon}>&#128196;</span>
                  <span className={styles.docSidebarName}>{doc.documentName}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className={styles.editorContainer}>
          <ReviewDocumentSurface
            token={token}
            session={session}
            activeDocument={activeDocument}
            resolvedDocId={resolvedDocId}
          />
        </div>
      </div>

      <ChatWidget
        targetType={session.targetType}
        targetId={session.targetId ?? ''}
        reviews={reviews}
        mode="reviewer"
        token={token}
      />
    </div>
  );
}

// ── Document Surface: branches PDF (PdfReviewViewer) vs DOCX (OnlyOffice) ─────

import type { ReviewSession, ReviewDocumentDto, ReviewDocumentVersion } from '@workpay/ds-shared/types/review';

interface ReviewDocumentSurfaceProps {
  token: string;
  session: ReviewSession;
  activeDocument: ReviewDocumentDto | null;
  resolvedDocId: string | null;
}

function ReviewDocumentSurface({
  token,
  session,
  activeDocument,
  resolvedDocId,
}: ReviewDocumentSurfaceProps) {
  /**
   * Which renderer this document needs.
   *
   * Read off the DOCUMENT whenever the session carries one, and off the session only when it
   * does not. The split was `isMultiDoc ? document : session`, which is a different question:
   * an ENVELOPE review with exactly ONE document is not multi-doc, but it still carries its
   * type and versions per document and leaves the session-level fields null. So a
   * one-document signing request read `session.fileType` = null, decided its PDF was not a
   * PDF, and handed it to OnlyOffice — which answers
   * `43058 K_DOCUMENT_FORMAT_NOT_EDITABLE` (`OnlyOfficeEditorConfigServiceImpl.requireDocx`)
   * and leaves the reviewer at "Failed to load editor" with no way to review at all. Only a
   * TEMPLATE review has no `documents`, which is the case the session-level fallback is for.
   */
  const activeFileType = (activeDocument?.fileType ?? session.fileType)?.toLowerCase();
  const isPdf = activeFileType === 'pdf';

  const versions: ReviewDocumentVersion[] = useMemo(
    () => activeDocument?.versions ?? session.versions ?? [],
    [activeDocument?.versions, session.versions],
  );

  if (isPdf && versions.length > 0) {
    return (
      <PdfReviewViewer
        key={`pdf-review-${resolvedDocId ?? 'single'}`}
        mode="reviewer"
        token={token}
        reviewDocumentId={resolvedDocId ?? undefined}
        currentReviewerId={session.reviewerId}
        versions={versions}
        resolveFileUrl={resolveReviewerFileUrl}
      />
    );
  }

  // Default to the existing OnlyOffice mount for DOCX or legacy reviews
  // where fileType isn't yet populated.
  return (
    <ReviewEditorMount
      token={token}
      documentId={resolvedDocId ?? undefined}
    />
  );
}

// ── OnlyOffice Editor Mount ──────────────────────────────────────────

function ReviewEditorMount({ token, documentId }: { token: string; documentId?: string }) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const editorRef = useRef<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { onlyOfficeUrl } = useDsConfig();
  const API_SCRIPT_URL = `${onlyOfficeUrl}/web-apps/apps/api/documents/api.js`;

  useEffect(() => {
    let destroyed = false;

    async function init() {
      try {
        setLoading(true);
        setError(null);

        const config = await reviewService.getReviewEditorConfig(token, documentId);
        if (destroyed) return;

        await loadScript(API_SCRIPT_URL);
        if (destroyed) return;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const DocsAPI = (window as any).DocsAPI;
        if (!DocsAPI) throw new Error(t('K_DS_EXT_REVIEW_EDITOR_ERR_API_NOT_LOADED', 'OnlyOffice API not loaded'));
        if (!containerRef.current) return;

        containerRef.current.innerHTML = '';
        const editorDiv = document.createElement('div');
        editorDiv.id = 'review-editor-' + Date.now();
        editorDiv.style.width = '100%';
        editorDiv.style.height = '100%';
        containerRef.current.appendChild(editorDiv);

        editorRef.current = new DocsAPI.DocEditor(editorDiv.id, {
          ...config,
          height: '100%',
          width: '100%',
          events: {
            onDocumentReady: () => { if (!destroyed) setLoading(false); },
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onError: (event: any) => { if (!destroyed) setError(event.data?.errorDescription || t('K_DS_EXT_REVIEW_EDITOR_ERR_GENERIC', 'Editor error')); },
          },
        });
      } catch (err: unknown) {
        if (!destroyed) {
          setError(err instanceof Error ? err.message : t('K_DS_EXT_REVIEW_EDITOR_ERR_LOAD_FAILED', 'Failed to load editor'));
          setLoading(false);
        }
      }
    }

    init();

    return () => {
      destroyed = true;
      if (editorRef.current) {
        try { editorRef.current.destroyEditor(); } catch { /* ignore */ }
        editorRef.current = null;
      }
      if (containerRef.current) containerRef.current.innerHTML = '';
    };
  }, [token, documentId, API_SCRIPT_URL, t]);

  return (
    <>
      {loading && <div className={styles.editorLoading}>{t('K_DS_EXT_REVIEW_EDITOR_LOADING', 'Loading document editor...')}</div>}
      {error && <div className={styles.error}><p>{t('K_DS_EXT_REVIEW_EDITOR_ERR_LOAD_FAILED', 'Failed to load editor')}</p><p>{error}</p></div>}
      <div ref={containerRef} style={{ width: '100%', height: '100%', display: loading || error ? 'none' : 'block' }} />
    </>
  );
}

// ── Script loader ────────────────────────────────────────────────────

let scriptLoaded = false;
let scriptLoading = false;
let scriptError: Error | null = null;
const scriptCallbacks: { resolve: () => void; reject: (err: Error) => void }[] = [];

function loadScript(url: string): Promise<void> {
  if (scriptLoaded) return Promise.resolve();
  if (scriptError) return Promise.reject(scriptError);
  return new Promise((resolve, reject) => {
    if (scriptLoading) { scriptCallbacks.push({ resolve, reject }); return; }
    scriptLoading = true;
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = () => {
      scriptLoaded = true;
      scriptLoading = false;
      resolve();
      scriptCallbacks.forEach((cb) => cb.resolve());
      scriptCallbacks.length = 0;
    };
    script.onerror = () => {
      scriptLoading = false;
      scriptError = new Error(`Failed to load script: ${url}`);
      reject(scriptError);
      scriptCallbacks.forEach((cb) => cb.reject(scriptError!));
      scriptCallbacks.length = 0;
    };
    document.head.appendChild(script);
  });
}
